import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { getCategory, FORMAT_INFO, usd, isOpen, longDate, timeRange, shortDate } from "@/lib/training";
import { getReenroll, releaseCheckout } from "@/lib/checkout";
import { locationLine, DIRECTIONS_URL, FACILITY, FACILITY_ADDRESS } from "@/lib/location";
import { MapPin } from "lucide-react";
import OfferingOption from "./OfferingOption";
import InlineWaitlist from "./InlineWaitlist";
import RegistrationFlow from "./RegistrationFlow";
import RegistrationOptions, { dropinOffering } from "./RegistrationOptions";
import { StagesIntro, StagesMeasure, hasStages } from "./ProgramStages";
import { track } from "@/lib/track";

const LOGO = "https://customer-assets.emergentagent.com/job_data-driven-baseball/artifacts/94x88gnl_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2003_59_37%20PM.png";

function StepHead({ n, title }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/40"><span className="text-white">{n}</span> &nbsp;{title}</p>;
}

function Chip({ on, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
      className={`rounded-full px-5 py-3 text-sm font-bold transition-colors ${on ? "bg-white text-[#0B0F19]" : "border border-white/20 text-white/75 hover:border-white/50"}`}>{children}</button>
  );
}

function Summary({ o, category, onRegister }) {
  const dates = o.session_dates || [];
  const rows = [
    ["Program", category.name], ["Age division", o.age_group], ["Format", FORMAT_INFO[o.format].label],
    ["Dates", dates.length > 1 ? `${shortDate(dates[0])} – ${shortDate(dates[dates.length - 1])} (${dates.length} sessions)` : longDate(o.start_date)],
    ["Time", `${timeRange(o)} Central`], ["Location", locationLine(o.location)],
  ];
  return (
    <div data-testid="registration-summary" className="rounded-3xl bg-white text-[#0B0F19] p-5 md:p-7">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Registration summary</p>
      <dl className="mt-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 py-2 border-b border-slate-100">
            <dt className="text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">{k}</dt>
            <dd data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-semibold text-right">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total due</p>
          <p data-testid="summary-total" className="text-3xl font-extrabold tracking-tighter">{usd(o.price)}</p>
        </div>
        {onRegister && (
          <button data-testid="register-now-btn" onClick={onRegister} className="inline-flex items-center gap-2 rounded-full bg-[#0B0F19] hover:bg-[#2563EB] text-white font-bold px-7 py-4 transition-colors">
            Register Now <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function TrainingPage() {
  const { category: key } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reenroll, setReenroll] = useState(null);
  const [registering, setRegistering] = useState(false);
  const age = params.get("age") || "";
  const format = params.get("format") || "";
  const offeringId = params.get("offering") || "";
  const optParam = params.get("option") || "";
  const sessParam = params.get("session") || "";
  const token = params.get("reenroll") || "";
  const update = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    n.delete("checkout");
    setParams(n, { replace: true });
    setRegistering(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (params.get("checkout") === "cancelled" && params.get("rid")) releaseCheckout(params.get("rid")).catch(() => {});
    getCategory(key).then(setData).catch(() => setError("This training category isn't available."));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) getReenroll(token).then(setReenroll).catch((e) => setError(e?.response?.data?.detail || "This re-enrollment link is no longer valid."));
  }, [token]);

  const offerings = useMemo(() => (data?.offerings || []).filter((o) => o.format !== "four_week" || o.full_enabled !== false || (o.dropin_schedule || []).length), [data]);
  const legacy = offeringId.includes(":") ? offeringId.split(":") : null;  // old drop-in links: ?offering=<program>:<session>
  const preset = offerings.find((o) => o.id === (legacy ? legacy[0] : offeringId));
  const ages = useMemo(() => [...new Set(offerings.map((o) => o.age_group).filter(Boolean))], [offerings]);
  const selAge = preset?.age_group || (ages.length === 1 ? ages[0] : ages.includes(age) ? age : "");
  const byAge = offerings.filter((o) => !selAge || o.age_group === selAge);
  const formats = ["trial", "four_week"].filter((f) => byAge.some((o) => o.format === f));
  const selFormat = preset?.format || (formats.length === 1 ? formats[0] : formats.includes(format) ? format : "");
  const options = selAge && selFormat ? byAge.filter((o) => o.format === selFormat) : [];
  const picked = preset && options.includes(preset) ? preset : options.length === 1 ? options[0] : null;
  const isFour = picked?.format === "four_week";
  const fullOk = isFour && picked.full_enabled !== false;
  const dropOk = isFour && (picked.dropin_schedule || []).length > 0;
  const option = !isFour ? "" : legacy ? "dropin" : optParam === "dropin" && dropOk ? "dropin" : optParam === "full" && fullOk ? "full" : fullOk && !dropOk ? "full" : dropOk && !fullOk ? "dropin" : "";
  const dsess = option === "dropin" ? (picked.dropin_schedule || []).find((s) => s.id === (legacy ? legacy[1] : sessParam) && s.status === "available") : null;
  const selected = !picked ? null : !isFour ? picked : option === "full" ? picked : dsess ? dropinOffering(picked, dsess) : null;
  useEffect(() => { track("program_view", key); }, [key]);
  useEffect(() => { if (selected) track("option_selected", key); }, [selected?.id, dsess?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dateStep = (ages.length > 1 ? 1 : 0) + (selAge ? 1 : 0) + 1;
  const regStep = String(dateStep + 1 + (isFour ? 1 : 0));

  if (error && !data) return <Shell><p data-testid="training-error" className="mt-16 text-lg font-semibold text-white/70">{error} <Link to="/#training" className="underline">View all training</Link></p></Shell>;
  if (!data) return <Shell><div className="mt-24 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div></Shell>;
  const c = data.category;
  return (
    <Shell>
      <h1 data-testid="training-title" className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase leading-[0.92]">{c.name}</h1>
      <p className="mt-4 text-base font-medium text-white/60 max-w-xl">{c.short_description}</p>
      <p data-testid="training-location-line" className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-white/70">
        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-white/45" /> {FACILITY.name} · {FACILITY_ADDRESS}</span>
        <a data-testid="training-directions-link" href={DIRECTIONS_URL} target="_blank" rel="noreferrer" className="font-bold text-[#60a5fa] hover:text-white">Get Directions</a>
      </p>
      <StagesIntro k={key} />
      {params.get("checkout") === "cancelled" && <p data-testid="checkout-cancelled-note" className="mt-5 rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/70">Checkout was canceled — you weren't charged. Pick up where you left off below.</p>}
      {error && <p data-testid="reenroll-error" className="mt-5 rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/70">{error}</p>}

      {offerings.length === 0 ? (
        <div data-testid="training-empty" className="mt-12 rounded-3xl border border-white/10 p-8">
          <p className="text-xl font-bold">New sessions are coming soon.</p>
          <p className="mt-2 text-sm font-medium text-white/55">Email <a className="underline" href="mailto:info@replabbaseball.com">info@replabbaseball.com</a> to hear when registration opens.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-10">
          {ages.length > 1 && (
            <div data-testid="step-age">
              <StepHead n="1" title="Select age group" />
              <div className="mt-4 flex flex-wrap gap-2">{ages.map((a) => <Chip key={a} testid={`age-option-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} on={selAge === a} onClick={() => update({ age: a, offering: "" })}>{a}</Chip>)}</div>
            </div>
          )}
          {selAge && (
            <div data-testid="step-format">
              <StepHead n={ages.length > 1 ? "2" : "1"} title={`Select training format${ages.length === 1 ? ` · ${selAge}` : ""}`} />
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {formats.map((f) => {
                  const list = byAge.filter((o) => o.format === f);
                  const min = Math.min(...list.map((o) => Number(o.format === "four_week" && o.full_enabled === false ? o.dropin_price : o.price)));
                  const on = selFormat === f;
                  return (
                    <button key={f} type="button" data-testid={`format-option-${f}`} aria-pressed={on} onClick={() => update({ format: f, offering: "" })}
                      className={`text-left rounded-2xl border-2 p-5 transition-colors ${on ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/30"}`}>
                      <span className="block text-lg font-extrabold tracking-tight">{FORMAT_INFO[f].label}</span>
                      <span className="mt-1 block text-sm font-medium text-white/55">{FORMAT_INFO[f].blurb}</span>
                      <span className="mt-3 block text-2xl font-extrabold tracking-tight">{list.length > 1 && list.some((o) => Number(o.price) !== min) ? "From " : ""}{usd(min)}</span>
                      {f === "four_week" && list.some((o) => (o.dropin_schedule || []).length > 0) && <span data-testid="format-dropin-note" className="mt-1 block text-sm font-semibold text-[#60a5fa]">or {usd(Math.min(...list.filter((o) => (o.dropin_schedule || []).length > 0).map((o) => Number(o.dropin_price))))} single-class drop-in</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {selFormat && (
            <div data-testid="step-date">
              <StepHead n={String(dateStep)} title={selFormat === "four_week" ? "Select program" : "Select date & time"} />
              <div className="mt-4 space-y-3">{options.map((o) => <OfferingOption key={o.id} o={o} selected={picked?.id === o.id} onSelect={(x) => update({ offering: x.id, option: "", session: "" })} />)}</div>
            </div>
          )}
          {isFour && (
            <div data-testid="step-option">
              <StepHead n={String(dateStep + 1)} title="Choose your registration option" />
              <div className="mt-4"><RegistrationOptions o={picked} option={option} sessionId={dsess?.id || ""} onChange={(p) => update({ offering: picked.id, option: p.option ?? option, session: p.session ?? (p.option === "full" ? "" : sessParam) })} /></div>
            </div>
          )}
          {selected && (
            <div data-testid="step-register">
              <StepHead n={regStep} title="Register" />
              <div className="mt-4">
                <Summary o={selected} category={c} onRegister={isOpen(selected) && !registering ? () => { track("register_click", key); setRegistering(true); } : null} />
                {!isOpen(selected) && selected.display_status === "full" && selected.waitlist_enabled && <InlineWaitlist offering={selected} />}
                {!isOpen(selected) && selected.display_status !== "full" && <p data-testid="not-open-note" className="mt-4 text-sm font-semibold text-white/60">Registration for this session isn't open yet{selected.opens_at ? "" : " — check back soon"}.</p>}
              </div>
              {registering && isOpen(selected) && <RegistrationFlow key={selected.id} offering={selected} prefill={reenroll && reenroll.next_program_id === selected.id ? reenroll.prefill : null} reenrollToken={reenroll && reenroll.next_program_id === selected.id ? token : ""} />}
            </div>
          )}
        </div>
      )}
      <StagesMeasure k={key} />
      {!hasStages(key) && (c.full_description || c.focus?.length > 0) && (
        <div data-testid="category-details" className="mt-16 border-t border-white/10 pt-10">
          {c.full_description && <p className="text-base font-medium text-white/60 leading-relaxed max-w-2xl">{c.full_description}</p>}
          {c.focus?.length > 0 && <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">{c.focus.map((f) => <li key={f} className="text-sm font-semibold text-white/75 border-t border-white/10 pt-2">{f}</li>)}</ul>}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-[100svh] bg-[#0B0F19] text-white">
      <header className="sticky top-0 z-40 bg-[#0B0F19]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/#training" data-testid="training-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft size={16} /> All training</Link>
          <Link to="/" data-testid="training-home-logo"><img src={LOGO} alt="Rep Lab" className="h-10 w-auto mix-blend-screen" /></Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 pb-24">{children}</main>
    </div>
  );
}
